/**
 * Presentation helpers for the Teams utilisation figure (CWL-157).
 *
 * The bands match `ResourceUtilizationBar` in the calendar domain (over 100% =
 * over capacity, 90%+ = near capacity) rather than the old 60/80 thresholds,
 * which were tuned for a job-count ratio. Both figures are now booked hours over
 * available hours, so they should be coloured the same way.
 */
import type { TeamUtilization } from '../types/team';

export type UtilisationBand = 'none' | 'under' | 'near' | 'over';

/** Shown wherever a rate would be, when the period offers no working capacity. */
export const NO_CAPACITY_LABEL = 'No capacity in this period';

/**
 * A period with no capacity — no members, everyone on leave, or a window outside
 * working hours — has no meaningful rate, so it must not be drawn as "0% busy".
 */
export function hasCapacity(utilization: TeamUtilization): boolean {
  return utilization.capacityHours > 0;
}

export function utilisationBand(utilization: TeamUtilization): UtilisationBand {
  if (!hasCapacity(utilization)) return 'none';
  if (utilization.rate > 100) return 'over';
  if (utilization.rate >= 90) return 'near';
  return 'under';
}

export function utilisationTextClass(utilization: TeamUtilization): string {
  switch (utilisationBand(utilization)) {
    case 'over':
      return 'text-red-600 dark:text-red-400';
    case 'near':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'under':
      return 'text-green-600 dark:text-green-400';
    case 'none':
      return 'text-muted-foreground';
  }
}

export function utilisationBarClass(utilization: TeamUtilization): string {
  switch (utilisationBand(utilization)) {
    case 'over':
      return 'bg-red-500';
    case 'near':
      return 'bg-yellow-500';
    default:
      return 'bg-green-500';
  }
}

/** Bar width, clamped so an over-booked team doesn't overflow its track. */
export function utilisationBarWidth(utilization: TeamUtilization): string {
  if (!hasCapacity(utilization)) return '0%';
  return `${Math.min(Math.max(utilization.rate, 0), 100)}%`;
}

/** `"20%"`, or an em dash when the period has no capacity to measure against. */
export function formatUtilisationRate(utilization: TeamUtilization): string {
  if (!hasCapacity(utilization)) return '—';
  return `${Math.round(utilization.rate)}%`;
}

/**
 * The line that makes the figure legible: what was booked, what was available
 * and how many jobs it came from.
 */
export function formatUtilisationDetail(utilization: TeamUtilization): string {
  const jobs = `${utilization.jobCount} ${utilization.jobCount === 1 ? 'job' : 'jobs'}`;
  if (!hasCapacity(utilization)) {
    return `${utilization.bookedHours.toFixed(1)}h booked · no capacity · ${jobs}`;
  }
  return `${utilization.bookedHours.toFixed(1)}h booked of ${utilization.capacityHours.toFixed(1)}h capacity · ${jobs}`;
}
